import { useEffect, useMemo, useState } from 'react';
import {
  Buildings, Warning, ArrowsClockwise, LinkSimple, CheckSquare, Square, ArrowRight, Eye,
} from '@phosphor-icons/react';
import { fmtDate } from '@/lib/utils';
import { ReTramiteDialog } from '@/components/bootstrap/ReTramiteDialog';
import {
  BuscarLoteInline, ConfianzaChip, ModeloPicker, ModelosLine, TramiteBadge,
  selectedDeBusqueda, selectedDeLote, type SelectedLote,
} from '@/components/bootstrap/piezas';
import { useVincular } from '@/hooks/useBootstrap';
import type { ApiError } from '@/api/client';
import type { BandejaLote, BootstrapGrupo, LoteBusqueda, ReTramiteRequerido } from '@/types';

// ============================================================
// Tarjeta de un LOTE con TODOS sus códigos juntos — la unidad de decisión real
// de la maratón.
//
// Medido el 26-oct-2026 sobre producción: 199 códigos con candidato único
// apuntan a solo 86 lotes (20 lotes con 1 código, 39 con 2, 8 con 3, 18 con 4,
// 1 con 5). Como `UX_TramiteAD_Vigente` deja UN trámite vigente por lote,
// decidir de a un código era una trampa: el humano vinculaba el primero y los
// otros 2-4 reventaban con 409 de re-trámite, uno por uno, sin haber visto
// nunca el conjunto.
//
// Acá la pregunta se hace UNA vez y con todo a la vista: «¿estos 5 códigos son
// el mismo trámite?». Si sí (el caso normal: el APC y el CFIA del mismo trámite
// más los reintentos), se vinculan juntos. Si alguno es un trámite distinto
// (cliente nuevo), se desmarca y vuelve a la bandeja para resolverlo aparte.
//
// LA ETAPA SALE DEL CÓDIGO MÁS ALTO (regla de negocio): cuando hay varios del
// mismo tipo, el consecutivo mayor es el más reciente — el lote se volvió a
// tramitar, normalmente con otro modelo. Los códigos viejos se vinculan igual
// (quedan como procedencia) pero ya no empujan la etapa. Que uno viejo haya
// llegado más lejos NO es un error: ese trámite se reemplazó.
// ============================================================

// Fila de un código del lote, marcable. Muestra lo que importa para decidir si
// pertenece al mismo trámite: tipo de correo (la etapa), fechas y texto leído,
// y si es el código VIGENTE o uno anterior.
function CodigoRow({
  g, marcado, onToggle, soloUno, vigente, marcarVigencia,
}: {
  g: BootstrapGrupo; marcado: boolean; onToggle: () => void; soloUno: boolean;
  vigente: boolean; marcarVigencia: boolean;
}) {
  const f1 = fmtDate(g.fechaPrimera);
  const f2 = fmtDate(g.fechaUltima);
  const rango = f1 && f2 ? (f1 === f2 ? f1 : `${f1} → ${f2}`) : (f1 || f2);

  return (
    <div
      className={`flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border px-2 py-1.5 text-xs transition-colors ${
        marcado ? 'border-brand-green-200 bg-brand-green-100/10' : 'border-brand-gray-200 bg-white opacity-60'
      }`}
    >
      {/* Con un solo codigo no hay nada que marcar: el check seria decorativo. */}
      {soloUno ? (
        <CheckSquare size={15} weight="fill" className="shrink-0 text-brand-green-200" />
      ) : (
        <button
          type="button"
          onClick={onToggle}
          className="shrink-0 text-brand-gray-400 hover:text-brand-gray-500"
          title={marcado ? 'Quitar del trámite (vuelve a la bandeja)' : 'Sumar al mismo trámite'}
        >
          {marcado
            ? <CheckSquare size={15} weight="fill" className="text-brand-green-200" />
            : <Square size={15} weight="bold" />}
        </button>
      )}
      <span className="rounded bg-brand-gray-500 px-1 py-0.5 text-[9px] font-bold uppercase text-white">
        {g.tipoCodigo}
      </span>
      <span className="font-mono text-[13px] font-semibold text-brand-gray-500">{g.codigo}</span>
      {/* Cuál manda. Solo con 2+ códigos: con uno solo la etiqueta es ruido. */}
      {marcarVigencia && (
        vigente ? (
          <span
            className="rounded-full bg-brand-green-100/40 px-1.5 py-0.5 text-[9px] font-bold uppercase text-brand-gray-500"
            title="Es el consecutivo más alto de su tipo: el trámite más reciente. De acá sale la etapa."
          >
            vigente
          </span>
        ) : (
          <span
            className="rounded-full bg-brand-gray-100 px-1.5 py-0.5 text-[9px] font-medium uppercase text-brand-gray-400"
            title="Consecutivo anterior: se vincula igual (queda como procedencia del re-trámite) pero no define la etapa."
          >
            anterior
          </span>
        )
      )}
      <span className="text-[10px] text-brand-gray-400">
        {g.cantidadCorreos} {g.cantidadCorreos === 1 ? 'correo' : 'correos'}
      </span>
      {g.tipos.map(t => (
        <span key={t} className="rounded-full bg-brand-gray-100 px-1.5 py-0.5 text-[9px] font-medium text-brand-gray-400">
          {t}
        </span>
      ))}
      {g.textosLoteRaw.length > 0 && (
        <span className="font-mono text-[10px] text-brand-gray-400">«{g.textosLoteRaw.join(' · ')}»</span>
      )}
      {rango && <span className="ml-auto font-mono text-[10px] text-brand-gray-400">{rango}</span>}
    </div>
  );
}

// Boton para abrir un plano en pestaña nueva. Portado del ActionBtn de Adventas
// (LoteInfoPanel) a proposito: es el mismo gesto en la otra app, y el operador
// venia de ahi. Sin link queda deshabilitado — y eso YA ES informacion: dice
// que el plano no esta cargado, no que el boton este roto.
function PlanoBtn({ label, url }: { label: string; url?: string | null }) {
  const hay = !!url;
  return (
    <button
      type="button"
      onClick={() => hay && window.open(url!, '_blank', 'noopener,noreferrer')}
      disabled={!hay}
      title={hay ? `Abrir ${label}` : `${label} no disponible`}
      className="flex items-center justify-center gap-1 rounded-lg border border-brand-gray-200 py-1.5 text-[11px] font-semibold text-brand-gray-500 transition-colors hover:bg-brand-gray-100 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
    >
      <Eye size={13} weight="bold" />
      {label}
    </button>
  );
}

export function LoteCard({ lote }: { lote: BandejaLote }) {
  const soloUno = lote.grupos.length === 1;

  // Códigos marcados = los que van al MISMO trámite. Default: SOLO los vigentes
  // (el CFIA más alto + el APC más alto), que es el trámite de hoy. Los
  // «anterior» arrancan desmarcados a propósito: marcarlos también es válido
  // (quedan como procedencia y no mueven la etapa) pero es una decisión, no el
  // default — y así el ojo revisa de a un trámite por vez.
  const [marcados, setMarcados] = useState<Set<string>>(() => {
    const vig = lote.codigosVigentes ?? [];
    return new Set(vig.length > 0 ? vig : lote.grupos.map(g => g.codigo));
  });
  // Lote destino: el sugerido por el motor, o otro elegido con el buscador.
  const [selected, setSelected] = useState<SelectedLote>(() => selectedDeLote(lote));
  const [buscarAbierto, setBuscarAbierto] = useState(false);
  const [reTramiteOpen, setReTramiteOpen] = useState(false);

  const otroLote = selected.idLote !== lote.idLote;

  // Modelo (opcional, 9.3): reservado-si-existe, sino tramitado. Re-prefill al
  // cambiar de lote destino.
  const [idModelo, setIdModelo] = useState<number | null>(
    lote.idModeloReservado ?? lote.idModeloTramitado ?? null);
  useEffect(() => {
    setIdModelo(selected.idModeloReservado ?? selected.idModeloTramitado ?? null);
  }, [selected.idLote]); // eslint-disable-line react-hooks/exhaustive-deps

  // Copiar el modelo del trámite al «Modelo Tramitado» del lote
  // (Lotes.IDModDefecto). Default: SÍ cuando el lote no tiene ninguno (no hay
  // nada que pisar, y dejarlo vacío no le sirve a nadie), NO cuando ya tiene
  // otro — pisar un dato existente es decisión del operador, no un default.
  const [aplicarAlLote, setAplicarAlLote] = useState(false);
  useEffect(() => {
    setAplicarAlLote(selected.idModeloTramitado == null);
  }, [selected.idLote]); // eslint-disable-line react-hooks/exhaustive-deps

  const vincular = useVincular();

  const sel = useMemo(() => {
    const gs = lote.grupos.filter(g => marcados.has(g.codigo));
    return {
      grupos: gs,
      idsCorreo: gs.flatMap(g => g.idsCorreo),
      correos: gs.reduce((a, g) => a + g.cantidadCorreos, 0),
      tipos: [...new Set(gs.flatMap(g => g.tipos))],
      textos: [...new Set(gs.flatMap(g => g.textosLoteRaw))],
    };
  }, [lote.grupos, marcados]);

  const todos = sel.grupos.length === lote.grupos.length;
  const ninguno = sel.grupos.length === 0;

  // Códigos que MANDAN (los resuelve el backend: CFIA más alto + APC más alto).
  const vigentes = useMemo(() => new Set(lote.codigosVigentes ?? []), [lote.codigosVigentes]);

  // El estado que calculó el servidor vale mientras TODOS los vigentes estén
  // marcados: la etapa sale del código más alto de lo marcado, así que desmarcar
  // un vigente cambia el resultado. Marcar de más (un «anterior») NO lo cambia —
  // por eso la condición es sobre los vigentes y no sobre «todos».
  const mandaElVigente = vigentes.size > 0
    ? [...vigentes].every(c => marcados.has(c))
    : sel.grupos.length === lote.grupos.length;

  const err = vincular.error as ApiError | null;
  const reTramite: ReTramiteRequerido | null =
    vincular.isError && err?.status === 409 && !!(err.body as ReTramiteRequerido | undefined)?.tramiteVigente
      ? (err.body as ReTramiteRequerido)
      : null;
  const otroError = vincular.isError && !reTramite ? (err?.message ?? 'Error al vincular') : null;

  const f1 = fmtDate(lote.fechaPrimera);
  const f2 = fmtDate(lote.fechaUltima);
  const rango = f1 && f2 ? (f1 === f2 ? f1 : `${f1} → ${f2}`) : (f1 || f2);

  function toggle(codigo: string) {
    setMarcados(prev => {
      const next = new Set(prev);
      if (!next.delete(codigo)) next.add(codigo);
      return next;
    });
    vincular.reset();
  }
  function pickBusqueda(l: LoteBusqueda) {
    setSelected(selectedDeBusqueda(l));
    vincular.reset();
  }
  function onVincular() {
    if (ninguno) return;
    vincular.mutate({
      idsCorreo: sel.idsCorreo, idLote: selected.idLote, idModelo,
      aplicarModeloAlLote: idModelo != null && aplicarAlLote,
    });
  }

  return (
    <div className="flex flex-col gap-2.5 rounded-2xl border border-brand-gray-200 bg-white p-3.5 shadow-sm">
      {/* Cabecera: el LOTE es el titulo (antes lo era el codigo) */}
      <div className="flex flex-wrap items-center gap-2">
        <Buildings size={17} weight="bold" className="shrink-0 text-brand-gray-400" />
        <span className="font-mono text-sm font-semibold text-brand-gray-500">
          {lote.idBd ?? `Lote ${lote.idLote}`}
        </span>
        {lote.proyecto && (
          <span className="rounded-full bg-brand-gray-100 px-2 py-0.5 text-[11px] text-brand-gray-400">{lote.proyecto}</span>
        )}
        <ConfianzaChip confianza={lote.confianza} />
        {lote.tieneTramiteVigente && <TramiteBadge />}
        <span className="ml-auto flex items-center gap-2">
          <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${
            lote.cantidadCodigos > 1 ? 'bg-brand-yellow/30 text-brand-gray-500' : 'bg-brand-gray-100 text-brand-gray-400'
          }`}>
            {lote.cantidadCodigos} {lote.cantidadCodigos === 1 ? 'código' : 'códigos'}
          </span>
          <span className="text-[11px] text-brand-gray-400">{lote.cantidadCorreos} correos</span>
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <ModelosLine tramitado={lote.modeloTramitado} reservado={lote.modeloReservado} />
        {rango && <span className="font-mono text-[11px] text-brand-gray-400">{rango}</span>}
      </div>

      {/* Planos del lote, para comprobar sin salir a Ventas. Son del lote
          SUGERIDO: si el operador elige otro con el buscador, los ocultamos en
          vez de mostrar los del lote equivocado. */}
      {!otroLote && (
        <div className="grid grid-cols-3 gap-1.5">
          <PlanoBtn label="Catastro" url={lote.linkCatastro} />
          <PlanoBtn label="Tramitado" url={lote.linkTramitado} />
          <PlanoBtn label="Construido" url={lote.linkConstruido} />
        </div>
      )}

      {/* Banner re-tramite (9.6): el lote YA tiene vigente, vincular dara 409 */}
      {lote.tieneTramiteVigente && (
        <div className="flex items-start gap-1.5 rounded-lg bg-brand-yellow/15 px-2.5 py-1.5 text-[11px] text-brand-gray-500">
          <ArrowsClockwise size={14} weight="bold" className="mt-0.5 shrink-0 text-brand-yellow" />
          Este lote ya tiene trámite vigente — vincular va a pedir resolver re-trámite.
        </div>
      )}

      {/* Los codigos del lote. Con 2+ es LA decision de la tarjeta. */}
      <div className="flex flex-col gap-1.5">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-brand-gray-400">
            {soloUno ? 'Código' : '¿Son el mismo trámite?'}
          </span>
          {!soloUno && (
            <>
              <span className="text-[10px] text-brand-gray-400">
                vienen marcados solo los <span className="font-semibold">vigentes</span> (los que ponen la
                etapa); sumá los <span className="font-semibold">anteriores</span> si son el mismo trámite
              </span>
              <span className="ml-auto flex gap-1.5">
                <button
                  type="button"
                  onClick={() => { setMarcados(new Set(lote.codigosVigentes ?? [])); vincular.reset(); }}
                  className="text-[10px] font-medium text-brand-gray-400 underline hover:text-brand-gray-500"
                >
                  Solo vigentes
                </button>
                <button
                  type="button"
                  onClick={() => { setMarcados(new Set(lote.grupos.map(g => g.codigo))); vincular.reset(); }}
                  className="text-[10px] font-medium text-brand-gray-400 underline hover:text-brand-gray-500"
                >
                  Todos
                </button>
                <button
                  type="button"
                  onClick={() => { setMarcados(new Set()); vincular.reset(); }}
                  className="text-[10px] font-medium text-brand-gray-400 underline hover:text-brand-gray-500"
                >
                  Ninguno
                </button>
              </span>
            </>
          )}
        </div>
        {lote.grupos.map(g => (
          <CodigoRow
            key={g.codigo}
            g={g}
            soloUno={soloUno}
            vigente={vigentes.has(g.codigo)}
            marcarVigencia={!soloUno}
            marcado={marcados.has(g.codigo)}
            onToggle={() => toggle(g.codigo)}
          />
        ))}
        {/* Notas de triage de cualquiera de sus codigos (ej. "tipo no clasificado"). */}
        {[...new Set(lote.grupos.flatMap(g => g.notas))].map((n, i) => (
          <div key={i} className="flex items-start gap-1.5 rounded-lg bg-brand-yellow/15 px-2.5 py-1.5 text-[11px] text-brand-gray-500">
            <Warning size={13} weight="bold" className="mt-0.5 shrink-0 text-brand-yellow" />
            {n}
          </div>
        ))}
      </div>

      {/* Estado resultante. SOLO se afirma cuando van TODOS los codigos: con un
          subconjunto cambia cuál es el código más alto marcado, así que el
          número que calculó el servidor ya no aplica. Mentir acá sería peor que
          no decir nada. */}
      {lote.estadoSugerido && (
        <div className="flex flex-col gap-1 text-[11px] text-brand-gray-400">
          <div className="flex flex-wrap items-center gap-1.5">
            <ArrowRight size={12} weight="bold" className="shrink-0" />
            {mandaElVigente ? (
              <>
                <span>Quedará en</span>
                <span className="rounded-full bg-brand-gray-100 px-2 py-0.5 font-medium text-brand-gray-500">
                  {lote.estadoSugerido}
                </span>
                {lote.etapaSugerida && <span>· {lote.etapaSugerida}</span>}
                {lote.tipoMasAvanzado && <span className="font-mono text-[10px]">({lote.tipoMasAvanzado})</span>}
                {!soloUno && <span>— la etapa del código vigente</span>}
              </>
            ) : (
              <span>
                Desmarcaste un código <span className="font-semibold">vigente</span>: la etapa sale del más
                alto de lo que quede marcado, así que ya no es necesariamente
                {' '}<span className="font-medium text-brand-gray-500">{lote.estadoSugerido}</span>.
              </span>
            )}
          </div>

          {/* Re-trámite: un código ANTERIOR llegó más lejos. Es información, no
              un error — el trámite viejo avanzó más pero se reemplazó, así que
              la etapa que vale es la del código nuevo. Decirlo acá evita que el
              operador lo lea como "el motor retrocedió el lote". */}
          {mandaElVigente && lote.estadoPrevio && (
            <div className="flex items-start gap-1.5 rounded-lg bg-brand-gray-100 px-2.5 py-1.5 text-brand-gray-500">
              <ArrowsClockwise size={13} weight="bold" className="mt-0.5 shrink-0 text-brand-gray-400" />
              <span>
                Un código anterior había llegado a{' '}
                <span className="font-medium">{lote.estadoPrevio}</span>
                {lote.tipoMasAvanzadoPrevio && (
                  <span className="font-mono text-[10px]"> ({lote.tipoMasAvanzadoPrevio})</span>
                )}
                . Es <strong>re-trámite</strong>: el lote se volvió a tramitar (por ventas, normalmente con
                otro modelo), así que la etapa vigente es la del código más alto. El trámite viejo queda
                vinculado como procedencia.
              </span>
            </div>
          )}
        </div>
      )}

      {/* Escape de 9.4: el lote sugerido puede estar mal. */}
      <div className="flex flex-col gap-1.5">
        <button
          type="button"
          onClick={() => setBuscarAbierto(o => !o)}
          className="self-start text-[11px] text-brand-gray-400 hover:text-brand-gray-500"
        >
          {buscarAbierto ? '− Ocultar buscador' : '+ ¿Otro lote? Buscar manualmente'}
        </button>
        {buscarAbierto && <BuscarLoteInline onPick={pickBusqueda} selectedIdLote={selected.idLote} />}
        {otroLote && (
          <div className="flex items-start gap-1.5 rounded-lg bg-brand-yellow/15 px-2.5 py-1.5 text-[11px] text-brand-gray-500">
            <Warning size={13} weight="bold" className="mt-0.5 shrink-0 text-brand-yellow" />
            Vas a vincular a <span className="font-mono font-semibold">{selected.idBd ?? `lote ${selected.idLote}`}</span>,
            no al lote sugerido.
            <button
              type="button"
              onClick={() => { setSelected(selectedDeLote(lote)); vincular.reset(); }}
              className="font-semibold underline hover:text-brand-gray-400"
            >
              Volver al sugerido
            </button>
          </div>
        )}
      </div>

      <ModeloPicker lote={selected} idModelo={idModelo} onChange={setIdModelo} />

      {/* El modelo del trámite NO es el del lote: son dos campos y conviven.
          Este check es el puente, y solo aparece si hay modelo que copiar. */}
      {idModelo != null && (
        <label className="flex cursor-pointer items-start gap-2 rounded-lg bg-brand-gray-100/60 px-2.5 py-2">
          <input
            type="checkbox"
            checked={aplicarAlLote}
            onChange={e => setAplicarAlLote(e.target.checked)}
            className="mt-0.5 h-3.5 w-3.5 shrink-0 cursor-pointer accent-brand-green-100"
          />
          <span className="text-[11px] leading-tight text-brand-gray-500">
            Guardar también como <span className="font-semibold">Modelo Tramitado del lote</span>
            {selected.idModeloTramitado == null ? (
              <span className="text-brand-gray-400"> (hoy el lote no tiene ninguno)</span>
            ) : selected.idModeloTramitado !== idModelo ? (
              <span className="text-brand-yellow">
                {' '}— reemplaza el actual{selected.modeloTramitado ? ` (${selected.modeloTramitado})` : ''}
              </span>
            ) : (
              <span className="text-brand-gray-400"> (ya es ese: no cambia nada)</span>
            )}
          </span>
        </label>
      )}

      <button
        type="button"
        onClick={onVincular}
        disabled={ninguno || vincular.isPending || vincular.isSuccess}
        className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand-green-100 px-3 py-1.5 text-sm font-semibold text-black hover:bg-brand-green-200 disabled:opacity-50"
      >
        <LinkSimple size={15} weight="bold" />
        {vincular.isSuccess
          ? 'Vinculado ✓'
          : vincular.isPending
            ? 'Vinculando…'
            : ninguno
              ? 'Marcá al menos un código'
              : soloUno
                ? `Vincular a ${selected.idBd ?? `lote ${selected.idLote}`}`
                : `Vincular ${sel.grupos.length} de ${lote.cantidadCodigos} códigos como UN trámite`}
      </button>

      {/* Lo que queda afuera no se pierde: vuelve a la bandeja. Decirlo evita
          que el operador crea que desmarcar descarta los correos. */}
      {!todos && !ninguno && (
        <p className="text-[10px] text-brand-gray-400">
          Los {lote.cantidadCodigos - sel.grupos.length} códigos sin marcar <strong>no se pierden</strong>:
          quedan en la bandeja y este lote reaparece con ellos para resolverlos por el camino re-trámite.
        </p>
      )}

      {reTramite && (
        <div className="flex flex-col gap-1.5 rounded-lg bg-brand-yellow/15 px-2.5 py-2 text-[11px] text-brand-gray-500">
          <div className="flex items-start gap-1.5">
            <ArrowsClockwise size={14} weight="bold" className="mt-0.5 shrink-0 text-brand-yellow" />
            <span>El lote ya tiene un trámite vigente. Resolvé el re-trámite para continuar.</span>
          </div>
          <button
            type="button"
            onClick={() => setReTramiteOpen(true)}
            className="self-start font-semibold text-brand-gray-500 underline hover:text-brand-gray-400"
          >
            Resolver re-trámite →
          </button>
        </div>
      )}

      {otroError && (
        <div className="flex items-start gap-1.5 rounded-lg bg-brand-red-100/15 px-2.5 py-2 text-[11px] text-brand-red-200">
          <Warning size={14} weight="bold" className="mt-0.5 shrink-0" />
          {otroError}
        </div>
      )}

      {reTramiteOpen && reTramite && (
        <ReTramiteDialog
          vinculando={{
            idsCorreo: sel.idsCorreo,
            etiqueta: sel.grupos.length === 1
              ? `${sel.grupos[0].tipoCodigo} ${sel.grupos[0].codigo}`
              : `${sel.grupos.length} códigos: ${sel.grupos.map(g => g.codigo).join(' · ')}`,
            cantidadCorreos: sel.correos,
            tipos: sel.tipos,
            textosLoteRaw: sel.textos,
          }}
          idLote={selected.idLote}
          idBd={selected.idBd}
          proyecto={selected.proyecto}
          idModeloInicial={idModelo}
          tramiteVigente={reTramite.tramiteVigente}
          onClose={() => { setReTramiteOpen(false); vincular.reset(); }}
        />
      )}
    </div>
  );
}
